import { Component, OnInit, Inject, ViewChild} from '@angular/core';
import { UserDataService } from '../../services/user-data.service';
import { Observable, zip, BehaviorSubject, merge, of, Subscription } from 'rxjs';
import { User } from '../../models/user';
import { map, switchMap } from 'rxjs/operators';
import { UserEditComponent } from '../user-edit/user-edit.component';
import { Project } from '../../../project/models/project';
import { ProjectDataService } from '../../../project/services/project-data.service';
import { UserGroupDataService } from '../../services/user-group-data.service';
import { UserGroup } from '../../models/user-group';
import { ConfirmationDialogService } from '../../../../shared/services/confirmation-dialog.service';
import { TranslateService } from '@ngx-translate/core';
import { SelectItem } from 'primeng/api/selectitem';
import { MessageService } from 'primeng/api';
import { OnDestroy } from '@angular/core';
@Component({
  selector: 'app-user-list',
  template: require('./user-list.component.html'),
  styles: [require('./user-list.component.sass')]
})
export class UserListComponent implements OnInit, OnDestroy {

  tableConfiguration: any = {
    paginate: true,
    sortableMode: 'multiple',
    defaultPagination: 250,
    itemsPerPageOptions: [50,100,250,500],
    exportFilename: 'users'
  }

  columns: any[];

  isAdminOptions: SelectItem[];

  @ViewChild(UserEditComponent) userEdit: UserEditComponent;

  users$: Observable<User[] | null>;
  private readonly refreshUserList$ = new BehaviorSubject(undefined);
  
  projectId: number;

  projectUserGroups$: Observable<{
    project: Project,
    userGroups: UserGroup[]
  }[]>;

  languageChangeSubscription: Subscription;

  constructor(
    private userDataService: UserDataService,
    private projectDataService: ProjectDataService,
    private userGroupDataService: UserGroupDataService,
    @Inject('ProjectService') private projectService: any,
    private confirmationDialogService: ConfirmationDialogService,
    private translate: TranslateService,
    private messageService: MessageService

  ) {
    this.projectUserGroups$ = zip(this.projectDataService.getAllProjects(), this.userGroupDataService.getAllUserGroups()).pipe(
      map(([projects, userGroups]) => {
        return projects.map(project => {
          return {
            project,
            userGroups: userGroups.filter(userGroup => userGroup.projectId === project.id)
          }
        })
      })
    );
    this.users$ = this.refreshUserList$.pipe(
      switchMap(() => merge(
        of(null),
        this.userDataService.getAllUsers(this.projectId))
    ))

    this.languageChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.refreshUserTable();
    })
  }

  ngOnInit() {
    this.projectId = this.projectService.getSelectedProjectId();
    this.refreshUserTable();
   
  }

  refreshUserTable = () => {
    this.columns = [{
      header: this.translate.instant('I18N_GENERAL.NAME'),
      field: 'name',
      type: 'text'
    }, {
      header: this.translate.instant('I18N_USER.USERNAME'),
      field: 'username',
      type: 'text'
    }, {
      header: this.translate.instant('I18N_USER.EMAIL'),
      field: 'email',
      type: 'text',
      width: '300px'
    }, {
      header: this.translate.instant('I18N_USER.RESET_ON_LOGIN'),
      field: 'firstlogin',
      type: 'firstLogin',
      disableSearch: true
    }, {
      header: this.translate.instant('I18N_USER.ADMIN'),
      field: 'admin',
      type: 'isAdmin',
      disableSearch: true
    }, {
      header: this.translate.instant('I18N_GENERAL.ACTIONS'),
      type: null,
      disableSorting: true,
      disableSearch: true
    }]

    this.isAdminOptions = [{
      label: this.translate.instant('I18N_USER.ALL'),
      value: null
     },{
        label: this.translate.instant('I18N_USER.ADMIN'),
        value: true
      },
      {
        label: this.translate.instant('I18N_USER.NOT_ADMIN'),
        value: false
      }
    ];
  }

  async addUser() {
    await this.userEdit.showModal()
  }

  async editUser(userId: number) {
    const user = await this.userDataService.getUser(userId, this.projectId);
    await this.userEdit.showModal(user)
  }

  reload = () => {
    this.refreshUserList$.next(undefined);
  }

  onSaveUser = async (user: User) => {
    try {
      if (user && user.id) {
        await this.userDataService.updateUser(user, this.projectId);
      } else {
        await this.userDataService.createUser(user, this.projectId);
      }
      this.messageService.add({
        severity:'success',
        summary: this.translate.instant(user && user.id ? "I18N_USER_MANAGEMENT.EDIT_USER": "I18N_USER_MANAGEMENT.CREATE_USER"),
        detail:`${this.translate.instant("I18N_GENERAL.USER")} ${user.name} ${this.translate.instant(user && user.id ? "I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_UPDATED" : "I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_CREATED")} `
      });
      this.reload();
    } catch (error) {
      this.messageService.add({
        severity:'error',
        summary: this.translate.instant(user && user.id ? "I18N_USER_MANAGEMENT.EDIT_USER": "I18N_USER_MANAGEMENT.CREATE_USER"),
        detail: error && error.message
      });
    }

  }

  deleteUser = async (user: User) => {
    if (user && user.id) {
      const confirmed = await this.confirmationDialogService.confirm(
        this.translate.instant('I18N_USER_MANAGEMENT.DELETE_USER'),
        `${this.translate.instant('I18N_USER_MANAGEMENT.ARE_YOU_SURE_TO_DELETE_THIS_USER')} ${user.name}?`,
        'danger',
        this.translate.instant('I18N_USER_MANAGEMENT.DELETE_USER'),
        this.translate.instant('I18N_GENERAL.CANCEL'),
        "lg" );
      if (confirmed) {
        try {
          await this.userDataService.deleteUser(user.id, this.projectId);
          this.messageService.add({
            severity:'success',
            summary: this.translate.instant("I18N_USER_MANAGEMENT.DELETE_USER"),
            detail: `${this.translate.instant("I18N_GENERAL.USER")} ${user.name} ${this.translate.instant("I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_DELETED")} `
          });
          this.reload();
        } catch (err) {
          this.messageService.add({
            severity:'error',
            summary: this.translate.instant("I18N_USER_MANAGEMENT.DELETE_USER"),
            detail: err.message
          });
        }
      }
    }
  }


  resetUserPassword = async (user: User) => {
    if (user && user.id) {
      const confirmed = await this.confirmationDialogService.confirm(
        'Password Reset for ' + user.name,
        'Are you sure you want to reset his/her password?',
        'info',
        'Reset pass',
        'Cancel',
        "lg");
      if (confirmed) {
        try {
          await this.userDataService.resetUserPassword(user.id, this.projectId);
          this.messageService.add({
            severity:'success',
            summary: this.translate.instant("I18N_USER_MANAGEMENT.RESET_PASSWORD"),
            detail:`${this.translate.instant("I18N_USER_MANAGEMENT.RESET_PASSWORD_EMAIL_SENT_TO")} ${user.name}`
          });
        } catch (err) {
          this.messageService.add({
            severity:'error',
            summary: this.translate.instant("I18N_USER_MANAGEMENT.RESET_PASSWORD"),
            detail:err.message
          });
        }
      }
    }
  }

  ngOnDestroy() {
    this.languageChangeSubscription.unsubscribe();
  }

}
